import { ensureType } from '@app/utils/ensureType'
import { getServerContext } from '@app/utils/getServerContext'
import { invariant } from '@app/utils/invariant'

export type FirebaseConfig = {
  apiKey: string
  authDomain: string
  databaseURL: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

const DEFAULT_MAPS_STYLE_URL = 'https://maps.kidsout.ru/maps/style.json'

export interface Config {
  appRequestId: string
  port: string
  apiUrl: string
  webUrl: string
  secureCookie: boolean
  cookieDomain: string | null
  /** Prefix for every cookie name, ours and the backend's: `st_` on staging, none on prod. From `COOKIE_PREFIX`. */
  cookiePrefix: string
  fbClient: string | null
  fbScope: string | null
  vkClient: string | null
  vkScope: string | null
  cloudpayments: string
  stripe: string
  proxy: boolean
  yandexVerification: string | null
  googleSiteVerification: string | null
  metrikaCounterId: string | null
  gaTrackingCode: string | null
  firebase: FirebaseConfig
  sentry: boolean
  amplitude: string | null
  supportAvatarUrl: string
  mapsStyleUrl: string
  facebookPixelId: string | null
  telegramBotToken: string | null
  paypalClient: string | null
  /**
   * Due to restriction of Russian government, usage of products belonged to Meta Corporation
   * is forbidden and punishable by law. Flag can be toggled when ban will be lifted.
   */
  metaCorpEnabled: boolean
}

const config: Config = IS_BROWSER
  ? (() => {
      const rawState = getServerContext().storeState
      if (!rawState) invariant('Store state is missing')
      // config lives in the redux_slice container after the reducer→slice migration.
      // Read it directly from the serialized state to avoid importing the slice (which imports this module).
      return (rawState as any).redux_slice?.config ?? invariant('Config slice is missing from SSR state')
    })()
  : ensureType<Config>({
      appRequestId: '',
      port: process.env.SERVER_PORT ?? '4000',
      apiUrl: process.env.API_URL || invariant('Define API_URL'),
      webUrl: process.env.WEB_URL || invariant('Define WEB_URL'),
      secureCookie: process.env.NODE_ENV === 'production',
      cookieDomain: process.env.COOKIE_DOMAIN || null,
      cookiePrefix: process.env.COOKIE_PREFIX || '',
      fbClient: process.env.FB_CLIENT || null,
      fbScope: process.env.FB_SCOPE || null,
      vkClient: process.env.VK_CLIENT || null,
      vkScope: process.env.VK_SCOPE || null,
      cloudpayments: process.env.CLOUDPAYMENTS || invariant('Define CLOUDPAYMENTS'),
      stripe: process.env.STRIPE || invariant('Define STRIPE'),
      proxy: JSON.parse(process.env.PROXY ?? String(BUILD_MODE !== 'production')),
      yandexVerification: process.env.YANDEX_VERIFICATION || null,
      googleSiteVerification: process.env.GOOGLE_SITE_VERIFICATION || null,
      metrikaCounterId: process.env.METRIKA_COUNTER_ID || null,
      gaTrackingCode: process.env.GA_TRACKING_CODE || null,
      firebase: {
        apiKey: process.env.FIREBASE_API_KEY || invariant('Define FIREBASE_API_KEY'),
        authDomain: process.env.FIREBASE_AUTH_DOMAIN || invariant('Define FIREBASE_AUTH_DOMAIN'),
        databaseURL: process.env.FIREBASE_DATABASE_URL || invariant('Define FIREBASE_DATABASE_URL'),
        projectId: process.env.FIREBASE_PROJECT_ID || invariant('Define FIREBASE_PROJECT_ID'),
        storageBucket: process.env.FIREBASE_STORAGE_BUCKET || invariant('Define FIREBASE_STORAGE_BUCKET'),
        messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID || invariant('Define FIREBASE_MESSAGING_SENDER_ID'),
        appId: process.env.FIREBASE_APP_ID || invariant('Define FIREBASE_APP_ID'),
      },
      sentry: !!process.env.SENTRY_ORG,
      amplitude: process.env.AMPLITUDE || null,
      supportAvatarUrl: '/support-avatar.svg',
      mapsStyleUrl: process.env.MAPS_STYLE_URL || DEFAULT_MAPS_STYLE_URL,
      facebookPixelId: process.env.FACEBOOK_PIXEL_ID || null,
      telegramBotToken: process.env.TELEGRAM_BOT_TOKEN ?? null,
      paypalClient: process.env.PAYPAL_API_USER || null,
      metaCorpEnabled: process.env.METACORP_ENABLED === '1',
    })

export default config
